import { useCallback, useState } from "react";
import {
  DialogActions,
  DialogContent,
  Drawer,
  drawerClasses,
  IconButton,
  styled,
  Typography,
  Box,
} from "@mui/material";
import { Close } from "@mui/icons-material";

import { Chip } from "~/common/m3/Chip/Chip";
import { STICKY_TO_BOTTOM } from "~/common/components/icon/Elevation";
import { verticalStacker } from "~/styles/styledMixins";
import { Button } from "~/common/components/Button/Button";
import { isTechValueFilterCategory, type TechValueFilterCategoryData } from "~/Search/filterConfigModels";
import { useTranslation } from "react-i18next";
import { getIsClearEnabled, selectBreadcrumbs } from "~/Search/redux/searchSelectors";
import { useDispatch, useSelector } from "react-redux";
import { BreadcrumbType } from "~/utils/models";
import { applyTechValueGroup, clearTechValuesGroup } from "~/Search/redux/actions";
import { useIsMinTabletSize } from "~/common/hooks/useMediaQuery";
import CloseIcon from "@mui/icons-material/Close";
import { InnerTechFilterCategory } from "~/Search/components/Filters/components/TechFilter/TechFilterCategory";
import type { RootState } from "~/redux/store";
import { selectVisibleFilters } from "~/Search/redux/filterConfigSelectors";

export const TechCategoryChip = ({ index }: { index: number }) => {
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const isMinTablet = useIsMinTabletSize();
  const { t } = useTranslation();

  const handleClose = () => {
    setIsDrawerOpen(false);
  };

  const dispatch = useDispatch();

  const techFilterCategory: TechValueFilterCategoryData | undefined =
    useSelector(selectVisibleFilters).filter(isTechValueFilterCategory)[index];
  const breadcrumbs = useSelector(selectBreadcrumbs);

  const isClearEnabled = useSelector((state: RootState) =>
    getIsClearEnabled(state, techFilterCategory?.items.map((item) => item.title) ?? []),
  );

  const onClearClick = () => {
    dispatch(clearTechValuesGroup(techFilterCategory.items.map((item) => item.title)));
    setIsDrawerOpen(false);
  };

  const applyCb = useCallback(
    (e: React.FormEvent) => {
      e.preventDefault();
      dispatch(applyTechValueGroup());
      setIsDrawerOpen(false);
    },
    [dispatch],
  );

  if (!techFilterCategory) {
    return null;
  }

  const validTechValueCodes = techFilterCategory.items.flatMap((item) => item.uid);

  const breadcrumbLength = breadcrumbs.filter((breadcrumb) => {
    if (breadcrumb.type === BreadcrumbType.TECH_VALUE) {
      return validTechValueCodes.includes(breadcrumb.code);
    } else {
      return false;
    }
  }).length;
  let label = t(techFilterCategory.title);
  if (breadcrumbLength > 0) {
    label += ` (${breadcrumbLength})`;
  }

  let onClear = undefined;
  if (breadcrumbLength > 0) {
    onClear = () => dispatch(clearTechValuesGroup(techFilterCategory.items.map((item) => item.title)));
  }

  return (
    <>
      <Chip
        variant={"filter"}
        onClick={() => setIsDrawerOpen(true)}
        label={label}
        selected={breadcrumbLength > 0}
        onDelete={onClear}
        deleteIcon={<CloseIcon />}
      />

      <StyledDrawer anchor={isMinTablet ? "left" : "bottom"} open={isDrawerOpen} onClose={handleClose}>
        <DrawerContent sx={{ ...verticalStacker("24px") }}>
          <DrawerHeader>
            <Typography variant="titleLarge" sx={{ fontWeight: "bold" }}>
              {t(techFilterCategory.title)}
            </Typography>
            <IconButton onClick={handleClose} color={"secondary"} variant={"contained"}>
              <Close />
            </IconButton>
          </DrawerHeader>
          <form onSubmit={applyCb}>
            <Box sx={{ ...verticalStacker(), gap: "32px" }}>
              <InnerTechFilterCategory data={techFilterCategory} />
            </Box>
            <button hidden type={"submit"} />
          </form>
        </DrawerContent>

        <DrawerActions>
          <Button
            variant={"text"}
            fullWidth
            disabled={!isClearEnabled}
            onClick={onClearClick}
            data-test-id={`tech-filter-category-${techFilterCategory.facetId}-reset`}
          >
            {t("searchPage.refinement.button.clear")}
          </Button>
          <Button
            disabled={!isClearEnabled}
            fullWidth
            data-test-id={`tech-filter-category-${techFilterCategory.facetId}-apply`}
            onClick={applyCb}
          >
            {t("searchPage.refinement.button.apply")}
          </Button>
        </DrawerActions>
      </StyledDrawer>
    </>
  );
};

const StyledDrawer = styled(Drawer)(({ theme }) => ({
  [`.${drawerClasses.paper}`]: {
    [theme.breakpoints.down("tablet")]: {
      height: "70vh",
      maxHeight: "700px",
    },
    [theme.breakpoints.between("tablet", "desktop-s")]: {
      width: "45vw",
      maxWidth: "550px",
    },
  },
}));

const DrawerHeader = styled("div")({
  width: "100%",
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
});

const DrawerContent = styled(DialogContent)({
  padding: "16px",
});

const DrawerActions = styled(DialogActions)({
  padding: "8px 16px",
  boxShadow: STICKY_TO_BOTTOM,
});
